import React from 'react';
import AuthorisationDeniedWarning from './AuthorisationDeniedWarning';
import EmailUnlinkedWarning from './EmailUnlinkedWarning';
import IncompleteNotification from './IncompleteNotification';
import InitiatedByUniNotification from './InitiatedByUniNotification';
import SilentNotification from './SilentNotification';
import { AgentApplicantAuthorisationStatus, Application, ApplicationStatus, EmailStatus, GetStaffApplicationResponse, IUserRole } from 'api-paths-cas';
import { DuplicatesAlert } from '@/components/pages/applicant-dashboard/summary/show-alert/DuplicatesAlert';
import { DuplicateAlertIntegration } from '@/components/pages/applicant-dashboard/summary/show-alert/DuplicateAlertIntegration';

interface Props {
  application: Application;
  applicant: GetStaffApplicationResponse['applicant'];
  agentAccessStatus: AgentApplicantAuthorisationStatus | undefined;
  onEditDetails: () => void;
  onOpenApplication: () => void;
  onDuplicateDetails: () => void;
  onTryAgain: () => void;
}

const ShowAlerts: React.FC<Props> = ({
  application,
  applicant,
  agentAccessStatus,
  onEditDetails,
  onOpenApplication,
  onDuplicateDetails,
  onTryAgain,
}) => {
  const isNotSubmitted = [ApplicationStatus.InProgress, ApplicationStatus.Completed].includes(
    application?.applicationStatus,
  );

  // Get duplicate match info from applicant
  const { duplicateMatches, potentialDuplicate } = applicant;
  const hasDuplicates = duplicateMatches?.length > 0;

  const isAuthorisationDenied = agentAccessStatus === AgentApplicantAuthorisationStatus.AUTHORISATION_DENIED;
  const isEmailUnlinked =
    applicant?.email !== undefined && (!applicant?.email || applicant?.email === EmailStatus.NoEmail);
  const isInitiatedByUni =
    isNotSubmitted &&
    [IUserRole.Uni, IUserRole.UniAdmin, IUserRole.UniSecure, IUserRole.UniLite, IUserRole.Admin].includes(
      application?.initiatedByRole,
    );
  const isIncomplete = isNotSubmitted || !application?.studyHistory;
  const isSilentMode = false; // keep behavior consistent; derive from application if enabled in the future

  if (potentialDuplicate && hasDuplicates) {
    return <DuplicatesAlert onDetailsClick={onDuplicateDetails} />;
  }

  if (potentialDuplicate && !hasDuplicates) {
    return <DuplicateAlertIntegration onClick={onTryAgain} />;
  }

  if (isAuthorisationDenied) {
    return <AuthorisationDeniedWarning />;
  }
  if (isEmailUnlinked) {
    return <EmailUnlinkedWarning onEditDetails={onEditDetails} />;
  }
  if (isInitiatedByUni) {
    return (
      <>
        <InitiatedByUniNotification onOpenApplication={onOpenApplication} />
        {isSilentMode && <SilentNotification />}
      </>
    );
  }
  if (isIncomplete) {
    return <IncompleteNotification />;
  }
  return null;
};

export default ShowAlerts;
